import React, { useState } from "react";
import {
  Drawer,
  Button,
  Divider,
  Tag,
  Popconfirm,
  Select,
  Typography,
  Space,
  message,
  Rate,
  Input,
} from "antd";
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  TeamOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  TagOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  StarOutlined,
  CoffeeOutlined,
  BorderOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { Reservation, ReservationStatus } from "../../../../utils/api/reservationsAPI";
import { addGuestReview } from "../../../../utils/api/reservationsAPI";
import { STATUS_CONFIG } from "../ReservationCard/ReservationCard";
import "./ReservationDetails.scss";

const { Text, Title } = Typography;

type Props = {
  reservation: Reservation | null;
  open: boolean;
  onClose: () => void;
  onEdit?: (reservation: Reservation) => void;
  onDelete?: (id: string) => void;
  onStatusChange?: (id: string, status: ReservationStatus) => void;
  onReviewSaved?: (id: string, review: Reservation["guestReview"]) => void;
};

const ReservationDetails: React.FC<Props> = ({
  reservation,
  open,
  onClose,
  onEdit,
  onDelete,
  onStatusChange,
  onReviewSaved,
}) => {
  const { t } = useTranslation();
  const [changingStatus, setChangingStatus] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewWaiter, setReviewWaiter] = useState("");
  const [savingReview, setSavingReview] = useState(false);
  const [editingReview, setEditingReview] = useState(false);

  React.useEffect(() => {
    if (!reservation) return;
    setEditingReview(false);
    if (reservation.guestReview?.rating) {
      setReviewRating(reservation.guestReview.rating);
      setReviewComment(reservation.guestReview.comment || "");
      setReviewWaiter(reservation.guestReview.waiterName);
    } else {
      setReviewRating(0);
      setReviewComment("");
      setReviewWaiter("");
    }
  }, [reservation?._id]);

  if (!reservation) return null;

  const cfg = STATUS_CONFIG[reservation.status];

  const handleSaveReview = async () => {
    if (!reviewRating || !reviewWaiter.trim()) {
      message.warning(t("Please enter your name and a rating"));
      return;
    }
    setSavingReview(true);
    try {
      const res = await addGuestReview(reservation!._id, {
        rating: reviewRating,
        comment: reviewComment.trim() || undefined,
        waiterName: reviewWaiter.trim(),
      });
      message.success(t("Review saved"));
      onReviewSaved?.(reservation!._id, res.data.guestReview);
      setEditingReview(false);
    } catch {
      message.error(t("Failed to save review"));
    } finally {
      setSavingReview(false);
    }
  };

  const handleStatusChange = async (status: ReservationStatus) => {
    if (!onStatusChange) return;
    setChangingStatus(true);
    try {
      await onStatusChange(reservation._id, status);
    } finally {
      setChangingStatus(false);
    }
  };

  const DetailRow = ({
    icon,
    label,
    value,
  }: {
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
  }) => (
    <div className="rd-row">
      <span className="rd-row__icon">{icon}</span>
      <div className="rd-row__content">
        <span className="rd-row__label">{label}</span>
        <span className="rd-row__value">{value}</span>
      </div>
    </div>
  );

  const sourceLabels: Record<string, string> = {
    internal: t("Internal"),
    phone: t("Phone"),
    website: t("Website"),
    widget: t("Widget"),
  };

  return (
    <Drawer
      title={
        <div className="rd-header">
          <span className="rd-header__title">{t("Reservation Details")}</span>
          <span
            className="rd-header__status"
            style={{ color: cfg.color, background: cfg.bg }}
          >
            <span
              className="rd-header__dot"
              style={{ background: cfg.dot }}
            />
            {t(cfg.label)}
          </span>
        </div>
      }
      placement="right"
      onClose={onClose}
      open={open}
      width={380}
      className="reservation-details-drawer"
      footer={
        (onEdit || onDelete) ? (
          <div className="rd-footer">
            {onEdit && (
              <Button
                icon={<EditOutlined />}
                onClick={() => {
                  onEdit(reservation);
                  onClose();
                }}
              >
                {t("Edit")}
              </Button>
            )}
            {onDelete && (
              <Popconfirm
                title={t("Delete this reservation?")}
                onConfirm={() => {
                  onDelete(reservation._id);
                  onClose();
                }}
                okText={t("Delete")}
                okButtonProps={{ danger: true }}
                cancelText={t("Cancel")}
              >
                <Button danger icon={<DeleteOutlined />}>
                  {t("Delete")}
                </Button>
              </Popconfirm>
            )}
          </div>
        ) : null
      }
    >
      <div className="rd-body">
        {/* Guest info */}
        <div className="rd-section">
          <Title level={5} className="rd-section__title">
            {t("Guest Information")}
          </Title>

          <DetailRow
            icon={<UserOutlined />}
            label={t("Name")}
            value={<strong>{reservation.guestName}</strong>}
          />
          <DetailRow
            icon={<PhoneOutlined />}
            label={t("Phone")}
            value={
              <a href={`tel:${reservation.guestPhone}`}>
                {reservation.guestPhone}
              </a>
            }
          />
          {reservation.guestEmail && (
            <DetailRow
              icon={<MailOutlined />}
              label={t("Email")}
              value={
                <a href={`mailto:${reservation.guestEmail}`}>
                  {reservation.guestEmail}
                </a>
              }
            />
          )}
        </div>

        <Divider className="rd-divider" />

        {/* Reservation details */}
        <div className="rd-section">
          <Title level={5} className="rd-section__title">
            {t("Reservation")}
          </Title>

          <DetailRow
            icon={<CalendarOutlined />}
            label={t("Date")}
            value={dayjs(reservation.date).format("dddd, DD MMMM YYYY")}
          />
          <DetailRow
            icon={<ClockCircleOutlined />}
            label={t("Time")}
            value={<strong>{reservation.time}</strong>}
          />
          <DetailRow
            icon={<TeamOutlined />}
            label={t("Guests")}
            value={`${reservation.numberOfGuests} ${t("guests")}`}
          />
          {(reservation.tableNames?.length || reservation.tableName) && (
            <DetailRow
              icon={<BorderOutlined />}
              label={t("Table")}
              value={
                <strong>
                  {reservation.tableNames?.length
                    ? reservation.tableNames.join(", ")
                    : reservation.tableName}
                </strong>
              }
            />
          )}
          {reservation.waiterName && (
            <DetailRow
              icon={<CoffeeOutlined />}
              label={t("Waiter")}
              value={reservation.waiterName}
            />
          )}
          <DetailRow
            icon={<TagOutlined />}
            label={t("Source")}
            value={sourceLabels[reservation.source] || reservation.source}
          />
        </div>

        {reservation.note && (
          <>
            <Divider className="rd-divider" />
            <div className="rd-section">
              <Title level={5} className="rd-section__title">
                {t("Special Requests")}
              </Title>
              <div className="rd-note">
                <MessageOutlined />
                <span>{reservation.note}</span>
              </div>
            </div>
          </>
        )}

        <Divider className="rd-divider" />

        {/* Guest review */}
        <div className="rd-section">
          <Title level={5} className="rd-section__title">
            <StarOutlined style={{ marginRight: 6 }} />
            {t("Guest Review")}
          </Title>

          {reservation.guestReview?.rating && !editingReview ? (
            <div className="rd-review-saved">
              <Rate disabled value={reservation.guestReview.rating} className="rd-review-saved__stars" />
              {reservation.guestReview.comment && (
                <p className="rd-review-saved__comment">{reservation.guestReview.comment}</p>
              )}
              <div className="rd-review-saved__footer">
                <span className="rd-review-saved__meta">
                  {reservation.guestReview.waiterName} · {dayjs(reservation.guestReview.createdAt).format("DD.MM.YYYY HH:mm")}
                </span>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setReviewRating(reservation.guestReview!.rating);
                    setReviewComment(reservation.guestReview!.comment || "");
                    setReviewWaiter(reservation.guestReview!.waiterName);
                    setEditingReview(true);
                  }}
                >
                  {t("Edit")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="rd-review-form">
              <Input
                placeholder={t("Your name (waiter)")}
                value={reviewWaiter}
                onChange={(e) => setReviewWaiter(e.target.value)}
                className="rd-review-form__input"
              />
              <Rate value={reviewRating} onChange={setReviewRating} />
              <Input.TextArea
                placeholder={t("Comment (optional)")}
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                rows={3}
                className="rd-review-form__textarea"
              />
              <Button
                type="primary"
                block
                loading={savingReview}
                onClick={handleSaveReview}
              >
                {t("Save Review")}
              </Button>
              {editingReview && (
                <Button block onClick={() => setEditingReview(false)}>
                  {t("Cancel")}
                </Button>
              )}
            </div>
          )}
        </div>

        <Divider className="rd-divider" />

        {/* Status change */}
        {onStatusChange && (
          <div className="rd-section">
            <Title level={5} className="rd-section__title">
              {t("Update Status")}
            </Title>
            <Select
              value={reservation.status}
              onChange={handleStatusChange}
              loading={changingStatus}
              style={{ width: "100%" }}
              size="large"
            >
              <Select.Option value="pending">{t("Pending")}</Select.Option>
              <Select.Option value="confirmed">{t("Confirmed")}</Select.Option>
              <Select.Option value="seated">{t("Seated")}</Select.Option>
              <Select.Option value="completed">{t("Completed")}</Select.Option>
              <Select.Option value="paid">{t("Paid")}</Select.Option>
              <Select.Option value="waitlisted">{t("Waitlisted")}</Select.Option>
              <Select.Option value="cancelled">{t("Cancelled")}</Select.Option>
              <Select.Option value="no-show">{t("No Show")}</Select.Option>
            </Select>
          </div>
        )}

        {/* Metadata */}
        {(reservation.createdBy || reservation.createdAt) && (
          <>
            <Divider className="rd-divider" />
            <div className="rd-meta">
              {reservation.createdBy && (
                <Text type="secondary" className="rd-meta__item">
                  {t("Added by")}{" "}
                  <strong>
                    {reservation.createdBy.firstName}{" "}
                    {reservation.createdBy.lastName}
                  </strong>
                </Text>
              )}
              {reservation.createdAt && (
                <Text type="secondary" className="rd-meta__item">
                  {dayjs(reservation.createdAt).format("DD.MM.YYYY HH:mm")}
                </Text>
              )}
              {reservation.confirmedBy && (
                <Text type="secondary" className="rd-meta__item">
                  <CheckCircleOutlined style={{ color: "#10b981" }} />{" "}
                  {t("Confirmed by")}{" "}
                  <strong>
                    {reservation.confirmedBy.firstName}{" "}
                    {reservation.confirmedBy.lastName}
                  </strong>
                </Text>
              )}
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
};

export default ReservationDetails;
